// Template manager (Unit 36g).
//
// CRUD UI for the comment_templates table. Opened from Settings → General →
// [Manage comment templates…]. The picker is in `TemplatePicker.tsx`; this is
// the longer-form editor for label/body/type/language_tag.

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch, apiJson } from "../api/client";
import { useCommentTemplates } from "../api/hooks";
import type { CommentTemplate } from "../api/types";

import "./TemplateManager.css";

type Draft = Omit<CommentTemplate, "id" | "usage_count" | "created_at" | "updated_at"> & {
  id?: number;
};

const EMPTY_DRAFT: Draft = {
  label: "",
  body: "",
  type: "any",
  language_tag: null,
};

export function TemplateManager({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { data, mutate } = useCommentTemplates();
  const [draft, setDraft] = useState<Draft | null>(null);

  async function save() {
    if (!draft || !draft.label.trim() || !draft.body.trim()) return;
    if (draft.id == null) {
      await apiJson<CommentTemplate>("/api/comment_templates/", {
        method: "POST",
        body: JSON.stringify({
          label: draft.label,
          body: draft.body,
          type: draft.type,
          language_tag: draft.language_tag,
        }),
      });
    } else {
      await apiJson<CommentTemplate>(`/api/comment_templates/${draft.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          label: draft.label,
          body: draft.body,
          type: draft.type,
          language_tag: draft.language_tag,
        }),
      });
    }
    setDraft(null);
    void mutate();
  }

  async function remove(id: number) {
    await apiFetch(`/api/comment_templates/${id}`, { method: "DELETE" });
    void mutate();
  }

  return (
    <div className="template-mgr-backdrop" onClick={onClose}>
      <div className="template-mgr" onClick={(e) => e.stopPropagation()}>
        <header className="template-mgr__header">
          <h3>{t("settings.templates_title")}</h3>
          <button type="button" className="template-mgr__close" onClick={onClose}>
            ✕
          </button>
        </header>

        {draft ? (
          <div className="template-mgr__editor">
            <label>
              {t("settings.template_label")}
              <input
                type="text"
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                autoFocus
              />
            </label>
            <label>
              {t("settings.template_body")}
              <textarea
                rows={4}
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              />
            </label>
            <label>
              {t("settings.template_type")}
              <select
                value={draft.type}
                onChange={(e) =>
                  setDraft({ ...draft, type: e.target.value as CommentTemplate["type"] })
                }
              >
                <option value="any">any</option>
                <option value="language">language</option>
                <option value="content">content</option>
                <option value="note">note</option>
              </select>
            </label>
            <label>
              {t("settings.template_language_tag")}
              <select
                value={draft.language_tag ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    language_tag: e.target.value ? e.target.value : null,
                  })
                }
              >
                <option value="">—</option>
                <option value="en">en</option>
                <option value="de">de</option>
              </select>
            </label>
            <div className="template-mgr__editor-actions">
              <button type="button" onClick={() => setDraft(null)}>
                {t("common.cancel")}
              </button>
              <button type="button" className="template-mgr__save" onClick={save}>
                {t("common.save")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              className="template-mgr__new"
              onClick={() => setDraft({ ...EMPTY_DRAFT })}
            >
              + {t("settings.new_template")}
            </button>
            <ul className="template-mgr__list">
              {data?.templates.length === 0 && (
                <li className="template-mgr__empty">{t("settings.templates_empty")}</li>
              )}
              {data?.templates.map((tpl) => (
                <li key={tpl.id} className="template-mgr__item">
                  <div className="template-mgr__item-main">
                    <div className="template-mgr__item-label">
                      <strong>{tpl.label}</strong>
                      <span className="template-mgr__item-meta">
                        {tpl.type}
                        {tpl.language_tag ? ` · ${tpl.language_tag}` : ""}
                        {tpl.usage_count > 0 ? ` · ×${tpl.usage_count}` : ""}
                      </span>
                    </div>
                    <div className="template-mgr__item-body">{tpl.body}</div>
                  </div>
                  <div className="template-mgr__item-actions">
                    <button
                      type="button"
                      onClick={() =>
                        setDraft({
                          id: tpl.id,
                          label: tpl.label,
                          body: tpl.body,
                          type: tpl.type,
                          language_tag: tpl.language_tag,
                        })
                      }
                    >
                      ✎
                    </button>
                    <button type="button" onClick={() => void remove(tpl.id)}>
                      ✕
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
